import {
	BehaviorSubject,
	Observable,
	fromEvent,
	timer,
	map,
	takeUntil,
	defaultIfEmpty,
	distinctUntilChanged,
	shareReplay,
	tap,
	switchMap,
	of,
} from 'rxjs'
import { ThemeHereIAm, ThemeHereIAmEvent, ThemeWhereAreYou } from './theme.events'
import type { SchmancyThemeComponent } from './theme.component'
import type { TSchmancyTheme } from './theme.interface'
import { state } from '@schmancy/state'

interface ThemeSettings {
	scheme: 'dark' | 'light' | 'auto'
	color: string
}

// Device preference — survives clearStates() on an identity change (a
// different person signing in must not reset dark mode).
const THEME_STORAGE_KEY = 'schmancy/theme'
const ThemeContext = state<ThemeSettings>(THEME_STORAGE_KEY).local(
	{
		scheme: 'auto',
		color: '#6200ee',
	},
	{ keepOnClear: true },
)

class ThemeService {
	private static instance: ThemeService

	#theme$ = new BehaviorSubject<Partial<TSchmancyTheme>>({})
	#themeComponent$ = new BehaviorSubject<SchmancyThemeComponent | null>(null)
	#rootComponent: SchmancyThemeComponent | null = null
	#fullscreen$ = new BehaviorSubject<boolean>(false)
	#bottomOffset$ = new BehaviorSubject<number>(0)

	public readonly scheme$ = ThemeContext.$.pipe(
		map(settings => settings.scheme),
		distinctUntilChanged(),
		shareReplay(1),
	)

	public readonly color$ = ThemeContext.$.pipe(
		map(settings => settings.color),
		distinctUntilChanged(),
		shareReplay(1),
	)

	public readonly theme$ = this.#theme$.asObservable().pipe(
		distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
		shareReplay(1),
	)

	public readonly themeComponent$ = this.#themeComponent$.asObservable().pipe(distinctUntilChanged(), shareReplay(1))

	public readonly fullscreen$ = this.#fullscreen$.asObservable().pipe(distinctUntilChanged(), shareReplay(1))

	public readonly bottomOffset$ = this.#bottomOffset$.asObservable().pipe(distinctUntilChanged(), shareReplay(1))

	get scheme(): 'dark' | 'light' | 'auto' {
		return ThemeContext.value.scheme
	}

	get color(): string {
		return ThemeContext.value.color
	}

	get theme(): Partial<TSchmancyTheme> {
		return this.#theme$.getValue()
	}

	get themeComponent(): SchmancyThemeComponent | null {
		return this.#themeComponent$.getValue()
	}

	get fullscreen(): boolean {
		return this.#fullscreen$.getValue()
	}

	get bottomOffset(): number {
		return this.#bottomOffset$.getValue()
	}

	public readonly resolvedScheme$ = this.scheme$.pipe(
		switchMap(scheme => {
			if (scheme === 'auto') {
				const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
				return new Observable<'dark' | 'light'>(subscriber => {
					const handler = (e: MediaQueryListEvent) => {
						subscriber.next(e.matches ? 'dark' : 'light')
					}
					mediaQuery.addEventListener('change', handler)
					subscriber.next(mediaQuery.matches ? 'dark' : 'light')
					return () => mediaQuery.removeEventListener('change', handler)
				})
			}
			return of(scheme as 'dark' | 'light')
		}),
		distinctUntilChanged(),
		shareReplay(1),
	)

	constructor() {
		this.discoverTheme()
	}

	/** Discover the nearest theme component in the DOM. Callable again to refresh discovery. */
	public discoverTheme(): Observable<SchmancyThemeComponent | null> {
		return fromEvent<ThemeHereIAmEvent>(window, ThemeHereIAm)
			.pipe(
				takeUntil(timer(100)),
				map(e => e.detail.theme as SchmancyThemeComponent),
				defaultIfEmpty(null),
				tap(themeComponent => {
					if (themeComponent) {
						this.registerThemeComponent(themeComponent)
					}
				}),
			)
			.pipe(
				tap(() => {
					window.dispatchEvent(
						new CustomEvent(ThemeWhereAreYou, {
							bubbles: true,
							composed: true,
						}),
					)
				}),
				switchMap(() =>
					fromEvent<ThemeHereIAmEvent>(window, ThemeHereIAm).pipe(
						takeUntil(timer(100)),
						map(e => e.detail.theme as SchmancyThemeComponent),
						defaultIfEmpty(null),
						tap(themeComponent => {
							if (themeComponent) {
								this.registerThemeComponent(themeComponent)
							}
						}),
					),
				),
			)
	}

	/** Called by theme components on mount/update to sync context. @internal */
	public registerThemeComponent(component: SchmancyThemeComponent): void {
		if (component.scoped) return
		this.#themeComponent$.next(component)

		ThemeContext.set({ scheme: component.scheme, color: component.color })
		this.#theme$.next(component.theme)
	}

	/** Attach the mounted theme component so setScheme/setColor can drive it live.
	 *  Reference only — unlike registerThemeComponent it does NOT write ThemeContext:
	 *  the persisted device pref is owned exclusively by setScheme/setColor, so a
	 *  component attaching (or re-attaching) on mount never seeds or clobbers it.
	 *  An unset pref is not "nothing chosen yet, so mirror the mount" — it stays
	 *  unset until a real setScheme/setColor call makes it user intent. */
	public attachComponent(component: SchmancyThemeComponent): void {
		if (component.scoped) return
		this.#themeComponent$.next(component)
		if (component.root) this.#rootComponent = component
	}

	/** The mounted `root` theme — what an overlay mounted outside every theme asks
	 *  for its locale. @internal */
	get rootComponent(): SchmancyThemeComponent | null {
		return this.#rootComponent
	}

	/** Detach a theme component on unmount so setScheme/setColor never target a
	 *  removed element. Clears the reference only if it still points at this
	 *  component — a newer theme may have attached in the meantime. */
	public detachComponent(component: SchmancyThemeComponent): void {
		if (this.#themeComponent$.getValue() === component) {
			this.#themeComponent$.next(null)
		}
		if (this.#rootComponent === component) this.#rootComponent = null
	}

	/** Called internally by theme components. @internal */
	public updateTheme(values: {
		scheme?: 'dark' | 'light' | 'auto'
		color?: string
		theme?: Partial<TSchmancyTheme>
	}): void {
		const updates: Partial<ThemeSettings> = {}
		if (values.scheme !== undefined) {
			updates.scheme = values.scheme
		}
		if (values.color !== undefined) {
			updates.color = values.color
		}
		if (Object.keys(updates).length > 0) {
			ThemeContext.set(updates)
		}
		if (values.theme !== undefined) {
			this.#theme$.next(values.theme)
		}
	}

	public setScheme(scheme: 'dark' | 'light' | 'auto'): void {
		const component = this.themeComponent
		if (component) {
			component.scheme = scheme
			component.rememberChoice('scheme')
			ThemeContext.set({ scheme })
		} else {
			console.warn('No theme component found. Scheme change may not persist.')
			ThemeContext.set({ scheme })
		}
	}

	/** Hex color string. */
	public setColor(color: string): void {
		const component = this.themeComponent
		if (component) {
			component.color = color
			component.rememberChoice('color')
			ThemeContext.set({ color })
		} else {
			console.warn('No theme component found. Color change may not persist.')
			ThemeContext.set({ color })
		}
	}

	/** Whether dark mode is currently active — resolves 'auto' via system preference. */
	public isDarkMode(): Observable<boolean> {
		return this.resolvedScheme$.pipe(map(scheme => scheme === 'dark'))
	}

	/** If currently 'auto', defaults to 'light'. */
	public toggleScheme(): void {
		const currentScheme = this.scheme
		const newScheme = currentScheme === 'dark' ? 'light' : currentScheme === 'light' ? 'dark' : 'light' // If 'auto', default to 'light'
		this.setScheme(newScheme)
	}

	/** Current value of a CSS variable from the theme (name without the `--schmancy-` prefix). */
	public getCSSVariable(variableName: string): string {
		const component = this.themeComponent
		if (component) {
			const host = component.root ? document.body : (component.shadowRoot?.host as HTMLElement)
			if (host) {
				return getComputedStyle(host).getPropertyValue(`--schmancy-${variableName}`).trim()
			}
		}
		return ''
	}

	public watchCSSVariable(variableName: string): Observable<string> {
		return this.theme$.pipe(
			map(() => this.getCSSVariable(variableName)),
			distinctUntilChanged(),
		)
	}

	/** Emits a custom event navigation components can listen to. */
	public setFullscreen(value: boolean): void {
		this.#fullscreen$.next(value)

		window.dispatchEvent(
			new CustomEvent('fullscreen', {
				detail: value,
				bubbles: true,
				composed: true,
			}),
		)
	}

	public toggleFullscreen(): void {
		this.setFullscreen(!this.fullscreen)
	}

	/** Bottom offset for viewport calculations — used by navigation bars to inform the
	 *  fullHeight directive of reserved space. */
	public setBottomOffset(value: number): void {
		this.#bottomOffset$.next(value)
	}

	/** Batches fullscreen/scheme/color updates. */
	public next(values: { fullscreen?: boolean; scheme?: 'dark' | 'light' | 'auto'; color?: string }): void {
		if (values.fullscreen !== undefined) {
			this.setFullscreen(values.fullscreen)
		}
		if (values.scheme !== undefined) {
			this.setScheme(values.scheme)
		}
		if (values.color !== undefined) {
			this.setColor(values.color)
		}
	}

	/** @internal */
	static getInstance(): ThemeService {
		if (!ThemeService.instance) {
			ThemeService.instance = new ThemeService()
		}
		return ThemeService.instance
	}
}

/** Theme singleton — reads/mutates the active theme and persists the user's
 *  scheme choice. Components subscribe via `theme.theme$`. */
export const theme = ThemeService.getInstance()
export const schmancyTheme = theme
export default theme
